import { Text } from '@/components/ui/text';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { CitationHoverModal } from '@/pages/Chat/components/Chat/CitationHoverModal';
import { MarkdownText } from '@/shared/MarkdownText';
import { useMemo, type FC } from 'react';
import { type Options } from 'react-markdown';
import { Source } from './ChatContext';

export type AIMarkdownTextProps = Options & {
  tableCopy?: boolean;
  className?: string;
  sources?: Source[];
  size?: 'base' | 'sm';
  tableView?: boolean;
};

// Citation pattern: [[cite:id]] where id is alphanumeric
const CITATION_PATTERN = /\[\[cite:([a-zA-Z0-9_-]+)\]\]/g;

/**
 * Pre-process content to convert [[cite:id]] to a markdown link format
 * that react-markdown can handle: [•](cite:id)
 */
function preprocessCitations(content: string | undefined): string {
  if (!content) return '';
  const processed = content.replace(CITATION_PATTERN, '[*](cite_$1)');

  return processed;
}

export const AIMarkdownText: FC<AIMarkdownTextProps> = ({
  components,
  sources,
  children,
  size = 'base',
  tableView = false,
  ...props
}) => {
  // Pre-process the content to convert [[cite:id]] to link format
  const processedChildren = useMemo(() => {
    if (typeof children === 'string') {
      return preprocessCitations(children);
    }
    return children;
  }, [children]);

  // Create AI-specific components that extend the base components
  const aiComponents = useMemo(
    () => ({
      ...components,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      a: (linkProps: any) => {
        // react-markdown passes href directly in props
        const href = linkProps.href as string | undefined;

        if (!href) {
          return <p className="text-red-200">This link is not valid</p>;
        }

        // Handle AI citation links with new format: cite:id
        if (href.startsWith('cite_')) {
          const citationId = href.slice(5); // Remove 'cite_' prefix

          const source = sources?.find(source =>
            source.ids.includes(citationId)
          );

          const citationDisplayIndex = sources?.findIndex(source =>
            source.ids.includes(citationId)
          );

          if (
            !citationId ||
            !source ||
            !sources?.length ||
            !source.quotedText
          ) {
            // Show error indicator for broken citations
            return null;
          }

          const badge = (
            <span className="inline-flex items-center justify-center rounded bg-yellow-400 border-none w-5 h-5 align-baseline mx-1">
              <Text variant="body-xs" className="text-black leading-none">
                {citationDisplayIndex !== undefined
                  ? citationDisplayIndex + 1
                  : '?'}
              </Text>
            </span>
          );

          // Different modal props for file vs database sources
          if (source.type === 'file') {
            return (
              <CitationHoverModal
                type="file"
                page={Number(source.pageNumber)}
                quotedText={source.quotedText}
                sourceName={source.fileName}
                groupUname={source.groupUname}
              >
                {badge}
              </CitationHoverModal>
            );
          }

          // Database source
          if (source.type === 'database') {
            return (
              <CitationHoverModal
                type="database"
                sourceName={source.databaseName}
                databaseUrl={source.databaseUrl}
                quotedText={source.quotedText}
                toolName={source.toolName}
              >
                {badge}
              </CitationHoverModal>
            );
          }
        }

        // Handle other link types (mailto, external)
        if (href.startsWith('mailto:')) {
          const email = href.split('mailto:')[1];
          return <a href={href}>{email}</a>;
        }

        // External links
        return (
          <Tooltip>
            <TooltipTrigger asChild>
              <a href={href} rel="noreferrer" target="_blank">
                {href}
              </a>
            </TooltipTrigger>
            <TooltipContent>
              This link will redirect you to the external website
            </TooltipContent>
          </Tooltip>
        );
      },
    }),
    [sources, components]
  );

  return (
    <MarkdownText
      {...props}
      components={aiComponents}
      size={size}
      tableView={tableView}
    >
      {processedChildren}
    </MarkdownText>
  );
};
